import { useEffect, useRef, type KeyboardEvent } from "react";
import { TAB_SCROLL_PADDING } from "../constants";
import { tabs as copy } from "../content";

export interface Tab {
  id: string;
  name: string;
  meta: string;
  /** A CSS colour for the vehicle swatch; profiles have none. */
  color?: string;
}

interface Props {
  /** Names the group for assistive tech, e.g. "Vehicles". */
  label: string;
  tabs: Tab[];
  activeId: string;
  /** e.g. "Add vehicle"; shown as "+ Add vehicle" on wide screens and a bare "+" on phones. */
  addLabel: string;
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
  onAdd: () => void;
}

/**
 * The row of vehicle or profile chips.
 *
 * These are toggle buttons rather than ARIA tabs: each chip carries its own
 * Remove button, which a `role="tablist"` may not contain. Arrow keys still move
 * between them, so the row behaves the way the layout suggests it should.
 */
export function TabBar({ label, tabs, activeId, addLabel, onSelect, onRemove, onAdd }: Props) {
  const listRef = useRef<HTMLUListElement>(null);

  // On phones the tabs scroll sideways; keep the selected one in view.
  useEffect(() => {
    const list = listRef.current;
    const active = list?.querySelector<HTMLElement>(".tab.active");
    if (!list || !active || list.scrollWidth <= list.clientWidth) return;
    const a = active.getBoundingClientRect();
    const l = list.getBoundingClientRect();
    const { left: leftPad, right: rightPad } = TAB_SCROLL_PADDING;
    if (a.left < l.left + leftPad) list.scrollBy({ left: a.left - l.left - leftPad, behavior: "smooth" });
    else if (a.right > l.right - rightPad) list.scrollBy({ left: a.right - l.right + rightPad, behavior: "smooth" });
  }, [activeId, tabs.length]);

  /** Left/Right step through the chips, Home/End jump to the ends. */
  const onKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    const step = { ArrowLeft: -1, ArrowRight: 1 }[e.key];
    const to =
      step !== undefined
        ? tabs.findIndex((t) => t.id === activeId) + step
        : e.key === "Home"
          ? 0
          : e.key === "End"
            ? tabs.length - 1
            : -1;
    const next = tabs[to];
    if (!next || to < 0) return;
    e.preventDefault();
    onSelect(next.id);
    listRef.current?.querySelectorAll<HTMLElement>(".tab-select")[to]?.focus();
  };

  return (
    <div className="tab-bar">
      {/* The handler sits on the list so it catches key presses bubbling from any chip.
          The list itself is not the control: everything focusable inside it is a button,
          and this only delegates their key events. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <ul className="tab-list" ref={listRef} aria-label={label} onKeyDown={onKeyDown}>
        {tabs.map((t) => {
          const name = t.name || copy.untitled;
          const active = t.id === activeId;
          return (
            <li key={t.id} className={"tab" + (active ? " active" : "")}>
              <button type="button" className="tab-select" aria-pressed={active} onClick={() => onSelect(t.id)}>
                {t.color && <span className="tab-swatch" style={{ background: t.color }} aria-hidden="true" />}
                <span className="tab-name">{name}</span>
                <span className="tab-meta">{t.meta}</span>
              </button>
              {tabs.length > 1 && (
                <button
                  type="button"
                  className="tab-remove"
                  title={copy.remove}
                  aria-label={copy.removeNamed(name)}
                  onClick={() => onRemove(t.id)}
                >
                  <span aria-hidden="true">×</span>
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <button type="button" className="btn-add tab-add" aria-label={addLabel} title={addLabel} onClick={onAdd}>
        <span aria-hidden="true">+</span>
        <span className="add-text" aria-hidden="true"> {addLabel}</span>
      </button>
    </div>
  );
}
